import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useToggle } from 'react-use'
import clsx from 'clsx'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'
import { AnimatePresence, LayoutGroup, motion } from 'motion/react'
import { Trans } from '@lingui/react/macro'
import * as Dialog from '@radix-ui/react-dialog'
import { type QueryKey, useQueries } from '@tanstack/react-query'

import { trackEvent } from '@jotta/grpc-connect-client/clientEvent'
import { useFeatures } from '@jotta/grpc-connect-client/config'
import { useUsername } from '@jotta/grpc-connect-client/customer'
import {
  hasAdjustments,
  hasCrop,
  hasFilter,
  hasOverlay,
  hasRotate,
} from '@jotta/photo-editor/models'
import type { Photos } from '@jotta/types/Photos'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { Button } from '@jotta/ui/Button'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { RadixModal } from '@jotta/ui/RadixModal'
import SvgCaretLeft from '@jotta/ui/SvgCaretLeft'
import SvgCaretRight from '@jotta/ui/SvgCaretRight'
import SvgChatCircle from '@jotta/ui/SvgChatCircle'
import SvgInfo from '@jotta/ui/SvgInfo'
import SvgPhotoSearch from '@jotta/ui/SvgPhotoSearch'
import SvgSliders from '@jotta/ui/SvgSliders'
import SvgX from '@jotta/ui/SvgX'
import { useBrandStore } from '@jotta/ui/useBrandTheme'
import { Breakpoint, useBreakpoint } from '@jotta/ui/useBreakpoint'

import type { CollectionRef } from '../../api/useCollection'
import { useAlbumId } from '../../api/useCollection'
import { type CarouselPhoto, usePhoto } from '../../api/usePhoto'
import { PhotoComments } from '../Comments/PhotoComments'
import { useCommentsOpenState } from '../Comments/useCommentsOpenState'
import type { ActionDispatcherProps } from '../PhotoActions/PhotoActions'
import { PhotoAction } from '../PhotoActions/PhotoActions'
import type { PhotoActionType } from '../PhotoActions/PhotoActions.types'
import { PhotoInfo } from '../PhotoInfo/PhotoInfo'

import { MediaView, type MediaViewItem } from './MediaView'
import type { PhotoEditorOverlayHandle } from './PhotoEditorOverlay'
import {
  type NavigationDirection,
  useHeldNavigation,
} from './useHeldNavigation'

import styles from './MediaCarousel.module.scss'

const debug = Debug('jotta:photos:MediaCarousel')

const loadPhotoEditorOverlay = () => import('./PhotoEditorOverlay')
const LazyPhotoEditorOverlay = lazy(() =>
  loadPhotoEditorOverlay().then(module => ({
    default: module.PhotoEditorOverlay,
  })),
)

export interface MediaCarouselProps {
  open: boolean
  collectionRef: CollectionRef
  commentsGroupId?: string
  ids: readonly string[]
  items: readonly Photos.Media[]
  /**
   * Photos the opener already holds, keyed by `id`. Their slides render straight away
   * from these while the full photo (actions, info, comments) is still loading, and keep
   * the preview's small thumbnail after it has loaded (see `getMediaCarouselSlideItem`).
   */
  previewItems?: readonly MediaCarouselPreview[]
  currentId?: string
  keyField?: MediaCarouselKeyField
  itemQueryOptions?: (key: string) => MediaCarouselItemQueryOptions | undefined
  onNavigate?: (item?: { id: string; timestamp: bigint }) => void
  onCurrentMediaChange?: (id: string) => void
  onClose: (closed: boolean) => void
  onSlideChange?: (slider: string) => void
  slideShowMode?: boolean
  showOwnerName?: boolean
  showSimilarSearch?: boolean
  navOnQueryParam?: string
  /**
   * Which album the carousel is opened from. A person album's photos are md5s with no
   * photo behind them, which rules out the editor and every action that addresses a
   * photo in its collection.
   */
  actionScope?: MediaCarouselActionScope
  onTrackAction?: (actionType: PhotoActionType) => void
}

export type MediaCarouselActionScope = 'default' | 'people'

export type MediaCarouselPreview = MediaViewItem & { id: string }

export type MediaCarouselKeyField = 'id' | 'md5'

export interface MediaCarouselItemQueryOptions {
  queryKey: QueryKey
  queryFn: (context: { signal?: AbortSignal }) => Promise<unknown>
  select?: (data: unknown) => unknown
  staleTime?: number
  enabled?: boolean
}

function getMediaKey(
  item: Pick<CarouselPhoto, 'id' | 'md5'> | Photos.Media,
  key: MediaCarouselKeyField,
) {
  return item[key]
}

export function getMediaCarouselWindowKeys(
  ids: readonly string[],
  currentKey: string | undefined,
) {
  const index = currentKey ? ids.indexOf(currentKey) : -1

  if (index < 0) {
    return []
  }

  return ids.slice(Math.max(0, index - 1), index + 2)
}

export function getMediaCarouselRemovalTargetKey({
  nextKey,
  prevKey,
}: {
  nextKey?: string
  prevKey?: string
}) {
  return nextKey ?? prevKey
}

/**
 * What a slide shows: the loaded photo once there is one, else the preview. The preview's
 * small thumbnail URL is kept even then: the loaded photo's is signed separately, and
 * switching to it would reload the image under the large thumbnail.
 */
export function getMediaCarouselSlideItem(
  preview: MediaViewItem | undefined,
  item: CarouselPhoto | undefined,
): MediaViewItem | undefined {
  if (item && preview) {
    return { ...item, expiringSmallThumbUrl: preview.expiringSmallThumbUrl }
  }
  return item ?? preview
}

export function canEditMediaCarouselPhoto({
  actionScope,
  albumId,
  item,
  photoEditorEnabled,
  username,
}: {
  actionScope: MediaCarouselActionScope
  albumId: string | undefined
  item:
    | Pick<CarouselPhoto, 'isLivePhoto' | 'owner' | 'videoDurationSeconds'>
    | undefined
  photoEditorEnabled: boolean
  username: string | undefined
}) {
  return Boolean(
    actionScope !== 'people' &&
    albumId === undefined &&
    photoEditorEnabled &&
    item &&
    item.owner === username &&
    !(item.videoDurationSeconds && !item.isLivePhoto),
  )
}

function ActionButton({
  onClick,
  loading,
  label,
  icon,
  testid,
}: ActionDispatcherProps) {
  const md = useBreakpoint(Breakpoint.md)
  return (
    <Button
      className="btn btn-ghost text-current"
      loading={loading}
      onClick={onClick}
      icon={icon}
      data-testid={testid}
    >
      {md && label}
    </Button>
  )
}

export const MediaCarousel = observer<MediaCarouselProps>(
  function MediaCarousel({
    collectionRef,
    ids: idsParam,
    items,
    previewItems,
    onNavigate,
    onCurrentMediaChange,
    onClose,
    currentId,
    slideShowMode,
    commentsGroupId,
    showOwnerName = false,
    showSimilarSearch = false,
    navOnQueryParam,
    actionScope = 'default',
    onTrackAction,
    keyField = 'id',
    itemQueryOptions,
    ...props
  }) {
    const username = useUsername()
    const { photoEditorEnabled } = useFeatures()
    const md = useBreakpoint(Breakpoint.md)
    const [showInfoWindow, toggleInfoWindow] = useToggle(false)
    const [showPhotoComments, togglePhotoComments] = useCommentsOpenState()
    const [editorOpen, setEditorOpen] = useState(false)
    // Imperative handle on the editor overlay so carousel-level close
    // paths (Esc, outside-click via Radix) can route through the
    // editor's discard-confirm flow rather than dropping unsaved edits.
    const editorRef = useRef<PhotoEditorOverlayHandle>(null)
    const [playing, setPlaying] = useState(false)
    const [params, setParams] = useSearchParams()

    const ids = useMemo(() => Array.from(new Set(idsParam)), [idsParam])
    const itemMap = useMemo(
      () =>
        Object.fromEntries(
          items.map(item => [getMediaKey(item, keyField), item]),
        ),
      [keyField, items],
    )

    const currentKey = currentId
    const index = currentKey ? ids.indexOf(currentKey) : -1
    const prevIndex = index > 0 ? index - 1 : -1
    const nextIndex = index >= 0 && index < ids.length - 1 ? index + 1 : -1
    const prevKey = ids[prevIndex] || undefined
    const nextKey = ids[nextIndex] || undefined
    const prevButton = useRef<HTMLButtonElement>(null)
    const nextButton = useRef<HTMLButtonElement>(null)
    const navigate = useNavigate()

    const navigateTo = useCallback(
      (id: string) => {
        if (navOnQueryParam) {
          params.set(navOnQueryParam, id)
          setParams(params, { replace: true })
        } else {
          navigate(
            `./${id}${params.toString() ? `?${params.toString()}` : ''}`,
            {
              replace: true,
            },
          )
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [navOnQueryParam, params, setParams],
    )

    const step = useCallback(
      (direction: NavigationDirection) => {
        const targetKey = direction < 0 ? prevKey : nextKey
        if (!targetKey) {
          return false
        }
        ;(direction < 0 ? prevButton : nextButton).current?.focus()
        navigateTo(targetKey)
        return true
      },
      [navigateTo, nextKey, prevKey],
    )
    // While an arrow is held the carousel only shows what is already loaded or known from
    // the preview; fetching every photo passed on the way would queue up behind it.
    const { scrubbing, buttonProps: navButtonProps } = useHeldNavigation({
      onStep: step,
      keyboardEnabled: !editorOpen,
    })

    const itemQueryKeys = useMemo(
      () => getMediaCarouselWindowKeys(ids, currentKey),
      [currentKey, ids],
    )
    const itemQueries = useQueries({
      queries: itemQueryOptions
        ? itemQueryKeys.map(key => {
            const options = itemQueryOptions(key)
            return options
              ? {
                  ...options,
                  enabled: options.enabled ?? true,
                }
              : {
                  queryKey: ['MediaCarouselItem', key],
                  queryFn: async () => undefined,
                  enabled: false,
                }
          })
        : [],
    })
    const queriedItemMap = useMemo(
      () =>
        Object.fromEntries(
          itemQueries
            .map((query, index) => {
              const key = itemQueryKeys[index]
              return key && query.data
                ? [key, query.data as CarouselPhoto]
                : undefined
            })
            .filter(
              (entry): entry is [string, CarouselPhoto] => entry !== undefined,
            ),
        ),
      [itemQueries, itemQueryKeys],
    )

    const albumId = useAlbumId(collectionRef)

    const previewMap = useMemo(
      () => new Map((previewItems ?? []).map(preview => [preview.id, preview])),
      [previewItems],
    )
    // A held arrow only defers the full photo when a preview can stand in for it.
    const deferWhileScrubbing = (key: string | undefined) =>
      scrubbing && Boolean(key && previewMap.has(key))

    const fetchedCurrentItem = usePhoto(collectionRef, itemMap, currentKey, {
      enabled: Boolean(
        currentKey && !itemQueryOptions && !deferWhileScrubbing(currentKey),
      ),
      // just the (legacy) photo id is probably not enough to reliably fetch the photo in public or followed albums.
      // be defensive for now and hope that photo refs in the v2 api solves this issue!
      throwOnError: false,
    })
    const fetchedPrevItem = usePhoto(collectionRef, itemMap, prevKey, {
      enabled: Boolean(
        prevKey && !itemQueryOptions && !deferWhileScrubbing(prevKey),
      ),
      throwOnError: false, // see above
    })
    const fetchedNextItem = usePhoto(collectionRef, itemMap, nextKey, {
      enabled: Boolean(
        nextKey && !itemQueryOptions && !deferWhileScrubbing(nextKey),
      ),
      throwOnError: false, // see above
    })
    const currentItem = itemQueryOptions
      ? currentKey
        ? queriedItemMap[currentKey]
        : undefined
      : fetchedCurrentItem
    const prevItem = itemQueryOptions
      ? prevKey
        ? queriedItemMap[prevKey]
        : undefined
      : fetchedPrevItem
    const nextItem = itemQueryOptions
      ? nextKey
        ? queriedItemMap[nextKey]
        : undefined
      : fetchedNextItem
    // Memoized so a slide's MediaView only re-renders when its photo or preview changes.
    const prevSlideItem = useMemo(
      () =>
        getMediaCarouselSlideItem(
          prevKey ? previewMap.get(prevKey) : undefined,
          prevItem,
        ),
      [previewMap, prevKey, prevItem],
    )
    const currentSlideItem = useMemo(
      () =>
        getMediaCarouselSlideItem(
          currentKey ? previewMap.get(currentKey) : undefined,
          currentItem,
        ),
      [previewMap, currentKey, currentItem],
    )
    const nextSlideItem = useMemo(
      () =>
        getMediaCarouselSlideItem(
          nextKey ? previewMap.get(nextKey) : undefined,
          nextItem,
        ),
      [previewMap, nextKey, nextItem],
    )
    const slides = (
      [
        ['prev', prevKey, prevSlideItem],
        ['current', currentKey, currentSlideItem],
        ['next', nextKey, nextSlideItem],
      ] as const
    ).flatMap(([position, key, item]) =>
      key && item ? [{ position, key, item }] : [],
    )
    const hasCurrentSlide = slides.some(slide => slide.position === 'current')
    const hasPrevSlide = slides.some(slide => slide.position === 'prev')
    const hasNextSlide = slides.some(slide => slide.position === 'next')

    // The action bar keeps showing the last loaded photo while the next one loads, rather
    // than unmounting and remounting on every step. It is inert until it catches up, so
    // an action can never land on the photo that was just left.
    const [footerItem, setFooterItem] = useState(currentItem)
    if (currentItem && currentItem !== footerItem) {
      setFooterItem(currentItem)
    }
    const footerIsStale = !currentItem
    const footerPhotos = useMemo(
      () => (footerItem ? [footerItem] : []),
      [footerItem],
    )

    const currentNavigationKey = currentItem
      ? getMediaKey(currentItem, keyField)
      : currentKey
    const currentMediaId = currentItem?.id ?? currentId
    const currentTimestampNanos = currentItem?.timestampNanos
    const currentItemQuery =
      itemQueryOptions && currentKey
        ? itemQueries[itemQueryKeys.indexOf(currentKey)]
        : undefined

    if (currentItemQuery?.error) {
      throw currentItemQuery.error
    }

    useEffect(() => {
      if (currentMediaId && onNavigate && currentTimestampNanos) {
        onNavigate({
          id: currentMediaId,
          timestamp: currentTimestampNanos,
        })
      }
    }, [onNavigate, currentMediaId, currentTimestampNanos])

    useEffect(() => {
      if (currentNavigationKey && index >= 0) {
        onCurrentMediaChange?.(currentNavigationKey)
      }
    }, [currentNavigationKey, index, onCurrentMediaChange])

    const commentItemId = footerItem?.commentItemId

    // The editor needs a photo it can load and patch, which an album's and a person's
    // are not. Also gated by the `web.photo_editor` feature toggle.
    const canEditCurrentPhoto = canEditMediaCarouselPhoto({
      actionScope,
      albumId,
      item: footerItem,
      photoEditorEnabled,
      username,
    })

    const closeButton = useRef<HTMLButtonElement>(null)
    const prevSlide = useRef<HTMLDivElement>(null)
    const nextSlide = useRef<HTMLDivElement>(null)
    const currentSlide = useRef<HTMLDivElement>(null)
    const branding = useBrandStore()

    useEffect(() => {
      if (showInfoWindow) {
        togglePhotoComments(false)
      }
    }, [showInfoWindow, togglePhotoComments])
    useEffect(() => {
      if (showPhotoComments) {
        toggleInfoWindow(false)
      }
    }, [showPhotoComments, toggleInfoWindow])
    useEffect(() => {
      // Editor takes the whole viewport — close any side panels when opened.
      if (editorOpen) {
        toggleInfoWindow(false)
        togglePhotoComments(false)
      }
    }, [editorOpen, toggleInfoWindow, togglePhotoComments])

    const swipeOrigin = useRef(0)
    const offsetOrigin = useRef(0)

    // Layout effect: a swiped slide moves to its new position in the same commit, and
    // its leftover swipe transform must be cleared before that frame paints.
    useLayoutEffect(() => {
      const els = [
        prevSlide.current,
        currentSlide.current,
        nextSlide.current,
      ].filter((el): el is HTMLDivElement => Boolean(el))

      for (const el of els) {
        el.style.transition = ''
        el.style.transform = ''
      }

      swipeOrigin.current = 0
      offsetOrigin.current = 0
      // A video that was playing keeps its element as it moves off to the side (its
      // player pauses once inactive), but it no longer reports to us, so whatever was
      // playing is over as far as the controls are concerned.
      setPlaying(false)
    }, [currentKey])

    const navigateTimeout = useRef(0)

    const handleTouchStart: React.TouchEventHandler<HTMLDivElement> =
      useCallback(e => {
        swipeOrigin.current = e.touches[0].clientX

        if (navigateTimeout.current) {
          window.clearTimeout(navigateTimeout.current)
          navigateTimeout.current = 0
        }

        const els = [
          prevSlide.current,
          currentSlide.current,
          nextSlide.current,
        ].filter((el): el is HTMLDivElement => Boolean(el))

        offsetOrigin.current = 0
        if (currentSlide.current) {
          offsetOrigin.current =
            currentSlide.current.getBoundingClientRect().left
        }

        for (const el of els) {
          el.style.transition = ''
          el.style.transform = `translateX(${offsetOrigin.current}px)`
        }
      }, [])

    const handleTouchEnd: React.TouchEventHandler<HTMLDivElement> = useCallback(
      e => {
        const els = [
          prevSlide.current,
          currentSlide.current,
          nextSlide.current,
        ].filter((slide): slide is HTMLDivElement => Boolean(slide))

        const offsetLeft =
          currentSlide.current?.getBoundingClientRect().left || 0
        const width = document.body.clientWidth

        let transform = ''
        const offsetPercent = (offsetLeft / width) * 100
        const transitionTime = 300

        if (offsetPercent > 20 && prevKey && hasPrevSlide) {
          transform = 'translateX(100%)'
          navigateTimeout.current = window.setTimeout(() => {
            navigateTimeout.current = 0
            navigateTo(prevKey)
          }, transitionTime - 100)
        } else if (offsetPercent < -20 && nextKey && hasNextSlide) {
          transform = 'translateX(-100%)'
          navigateTimeout.current = window.setTimeout(() => {
            navigateTimeout.current = 0
            navigateTo(nextKey)
          }, transitionTime - 100)
        }

        for (const el of els) {
          el.style.transition = `transform ${transitionTime}ms`
          el.style.transform = transform
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [hasNextSlide, hasPrevSlide, navigateTo, nextKey, prevKey],
    )

    const handleTouchMove: React.TouchEventHandler<HTMLDivElement> =
      useCallback(e => {
        const swipePixels =
          e.touches[0].clientX - swipeOrigin.current + offsetOrigin.current
        const els = [
          prevSlide.current,
          currentSlide.current,
          nextSlide.current,
        ].filter((el): el is HTMLDivElement => Boolean(el))

        for (const el of els) {
          el.style.transform = `translateX(${swipePixels}px)`
        }
      }, [])

    // Keep scroll position which is reset by video fullscreen
    useEffect(() => {
      const el = document.scrollingElement

      if (!currentMediaId || !el) {
        return
      }

      const scrollTop = el.scrollTop
      return () => {
        el.scrollTop = scrollTop
      }
    }, [currentMediaId])

    const handleClose = useCallback(
      (open: boolean) => {
        if (open) {
          return
        }

        if (editorOpen) {
          // Esc / outside-click while editor is open routes through the
          // editor's discard-confirm flow rather than dropping unsaved
          // edits. The editor pops the discard modal if there are any
          // edits; otherwise it calls back to `onClose` via the overlay
          // (which calls `setEditorOpen(false)`), giving us the same
          // close path the in-editor X button takes.
          editorRef.current?.requestCancel()
        } else if (showInfoWindow) {
          toggleInfoWindow(false)
        } else if (showPhotoComments) {
          togglePhotoComments(false)
        } else if (onClose) {
          onClose(false)
        }
      },
      [
        editorOpen,
        showInfoWindow,
        showPhotoComments,
        onClose,
        toggleInfoWindow,
        togglePhotoComments,
      ],
    )

    const handleRemoved = () => {
      const targetKey = getMediaCarouselRemovalTargetKey({ nextKey, prevKey })

      if (targetKey) {
        navigateTo(targetKey)
      } else {
        navigate('./', { replace: true })
      }
    }

    return (
      <RadixModal
        variant="dialogs.overlayFileViewer"
        onClose={handleClose}
        {...props}
      >
        <Dialog.Content
          // ref={dialogRef}
          onPointerDownOutside={e => {
            e.preventDefault()
          }}
          onOpenAutoFocus={e => {
            e.preventDefault()
            closeButton.current?.focus()
          }}
        >
          <Dialog.Title className="sr-only">
            <Trans>Media viewer</Trans>
          </Dialog.Title>
          <Dialog.Description className="sr-only">
            <Trans>Use left and right arrow keys to browse photos.</Trans>
          </Dialog.Description>
          <div
            data-testid="Carousel"
            id="carousel"
            className={styles.container}
          >
            <div
              onTouchStart={editorOpen ? undefined : handleTouchStart}
              onTouchMove={editorOpen ? undefined : handleTouchMove}
              onTouchEnd={editorOpen ? undefined : handleTouchEnd}
              className={styles.slideContainer}
            >
              <div className={styles.slides}>
                {/* Keyed siblings, so stepping moves a slide's element to its new position
                    rather than re-rendering each position with a different photo, which
                    would show a blank or stale image until the new one loads. */}
                {slides.map(({ position, key, item }) => (
                  <div
                    key={key}
                    ref={
                      position === 'prev'
                        ? prevSlide
                        : position === 'next'
                          ? nextSlide
                          : currentSlide
                    }
                    id={
                      position === 'current'
                        ? 'carousel-current-slide'
                        : undefined
                    }
                    className={
                      position === 'prev'
                        ? styles.slidePrev
                        : position === 'next'
                          ? styles.slideNext
                          : styles.slide
                    }
                  >
                    <MediaView
                      item={item}
                      active={position === 'current'}
                      scrubbing={scrubbing}
                      slideShowMode={slideShowMode}
                      setPlaying={
                        position === 'current' ? setPlaying : undefined
                      }
                    />
                  </div>
                ))}
                {prevIndex >= 0 && (
                  <button
                    id="carousel-prev-button"
                    ref={prevButton}
                    {...navButtonProps(-1)}
                    className={styles.prevButton}
                    style={{
                      opacity: slideShowMode || playing ? 0 : 1,
                    }}
                  >
                    <PlainBrandIcon icon={SvgCaretLeft} />
                  </button>
                )}
                {!hasCurrentSlide && (
                  <LoadingOverlay
                    id="MediaCarousel"
                    position="fixed"
                    open
                    darkMode
                  />
                )}
                {nextIndex >= 0 && (
                  <button
                    id="carousel-next-button"
                    {...navButtonProps(1)}
                    ref={nextButton}
                    className={styles.nextButton}
                    style={{
                      opacity: slideShowMode || playing ? 0 : 1,
                    }}
                  >
                    <PlainBrandIcon icon={SvgCaretRight} />
                  </button>
                )}
              </div>
              {footerItem && !playing && (
                <div
                  // eslint-disable-next-line tailwindcss/no-custom-classname
                  className={clsx(styles.footer, 'text-white', {
                    'sidepanel-visible': showInfoWindow || showPhotoComments,
                  })}
                  id="carousel-actions"
                  data-testid="CarouselActions"
                  inert={footerIsStale}
                  aria-busy={footerIsStale}
                >
                  <PhotoAction.Provider
                    collectionRef={collectionRef}
                    photos={footerPhotos}
                    hasMd5OnlyPhotos={actionScope === 'people'}
                    onTrackAction={onTrackAction}
                    actionDispatcherComponent={ActionButton}
                  >
                    {footerItem.owner === username && (
                      <Button
                        className="btn btn-ghost text-current"
                        icon={SvgPhotoSearch}
                        onClick={() =>
                          navigate(`/photo?id=${footerItem.idDeprecated}`)
                        }
                        data-testid="ACTION_FIND_SIMILAR"
                      >
                        {md && <Trans>Find similar</Trans>}
                      </Button>
                    )}
                    {canEditCurrentPhoto && (
                      <Button
                        className="btn btn-ghost text-current"
                        icon={SvgSliders}
                        onFocus={() => void loadPhotoEditorOverlay()}
                        onMouseEnter={() => void loadPhotoEditorOverlay()}
                        onClick={() => {
                          trackEvent('PHOTO_EDITOR_START')
                          setEditorOpen(true)
                        }}
                        data-testid="ACTION_EDIT_PHOTO"
                      >
                        {md && <Trans>Edit</Trans>}
                      </Button>
                    )}

                    {commentItemId && (
                      <Button
                        className="btn btn-ghost text-current"
                        icon={SvgChatCircle}
                        onClick={() => togglePhotoComments(true)}
                        data-testid="ACTION_ADD_COMMENT"
                      >
                        {md && <Trans>Comments</Trans>}
                      </Button>
                    )}
                    <PhotoAction.SharePhotos />
                    <PhotoAction.AddToAlbum />
                    <Button
                      className="btn btn-ghost text-current"
                      onClick={toggleInfoWindow}
                      icon={SvgInfo}
                      data-testid="ACTION_SHOW_INFO"
                    >
                      {md && <Trans>Show info</Trans>}
                    </Button>
                    {/* Hiding, removing and deleting all address a photo in its
                        collection, which a person's md5 cannot. */}
                    {actionScope === 'default' && (
                      <>
                        <PhotoAction.HidePhotos onSuccess={handleRemoved} />
                        <PhotoAction.UnhidePhotos onSuccess={handleRemoved} />
                        {albumId ? (
                          <PhotoAction.RemoveFromAlbum
                            onSuccess={handleRemoved}
                          />
                        ) : (
                          <PhotoAction.DeletePhotos onSuccess={handleRemoved} />
                        )}
                      </>
                    )}
                    <PhotoAction.DownloadPhotos />
                  </PhotoAction.Provider>
                </div>
              )}
              <Dialog.Close
                ref={closeButton}
                id="carousel-close-button"
                data-testid="CarouselClose"
                // eslint-disable-next-line tailwindcss/no-custom-classname
                className={clsx({
                  'carousel-close': true,
                  [styles.closeButton]: true,
                })}
              >
                <PlainBrandIcon icon={SvgX} />
                <span>
                  <Trans>Close</Trans>
                </span>
              </Dialog.Close>
              {editorOpen && currentItem && (
                <Suspense
                  fallback={
                    <LoadingOverlay
                      id="PhotoEditorOverlay"
                      open
                      position="absolute"
                      darkMode
                    />
                  }
                >
                  <LazyPhotoEditorOverlay
                    ref={editorRef}
                    item={currentItem}
                    onClose={reason => {
                      if (reason === 'cancel') {
                        trackEvent('PHOTO_EDITOR_CANCELLED')
                      }
                      setEditorOpen(false)
                    }}
                    onSaved={(newId, editState) => {
                      trackEvent('PHOTO_EDITOR_COMPLETE', {
                        filter: hasFilter(editState),
                        rotate: hasRotate(editState),
                        flip: editState.isFlipped,
                        crop: hasCrop(editState),
                        adjustment: hasAdjustments(editState),
                        overlay: hasOverlay(editState),
                      })
                      // The new photo has been imported — navigate the carousel
                      // to it, then dismiss the editor. addFromUpload has
                      // already added it to the timeline so it'll resolve via
                      // the normal usePhoto path.
                      navigateTo(newId)
                      setEditorOpen(false)
                    }}
                  />
                </Suspense>
              )}
            </div>

            <LayoutGroup>
              <AnimatePresence>
                {showInfoWindow && currentItem && (
                  <motion.div
                    className={styles.sidePanel}
                    initial={{ width: 0, opacity: 0 }}
                    animate={{
                      width: branding.isMobileTimeline
                        ? '100%'
                        : 'var(--carousel-sidepanel-width)',
                      opacity: 1,
                    }}
                    transition={{ duration: 0.2 }}
                    exit={{ width: 0, opacity: 0 }}
                  >
                    <PhotoInfo
                      media={currentItem}
                      onClose={toggleInfoWindow}
                      showOwnerName={showOwnerName}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
              <AnimatePresence>
                {showPhotoComments && currentItem && (
                  <motion.div
                    className={styles.sidePanel}
                    initial={{ width: 0, opacity: 0 }}
                    animate={{
                      width: branding.isMobileTimeline
                        ? '100%'
                        : 'var(--carousel-sidepanel-width)',
                      opacity: 1,
                    }}
                    transition={{ duration: 0.2 }}
                    exit={{ width: 0, opacity: 0 }}
                  >
                    <PhotoComments
                      contextKey={commentItemId}
                      albumContextKey={commentsGroupId}
                      onClose={() => togglePhotoComments(false)}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </LayoutGroup>
          </div>
        </Dialog.Content>
      </RadixModal>
    )
  },
)
